import Enumerable from "linq";

/** 文字列操作のユーティリティクラス */
export class StringUtility {
  /** 半角カタカナと全角カタカナの対応表 */
  /** 他に全角カタカナ扱いとする範囲に「ヰ ヱ ヽ ヿ」が含まれるが、これらは対応する半角文字が無い為変換しない */
  private static readonly _replaceTableHalfFullKatakana = [
    ["ｳﾞ", "ヴ"], ["ﾜﾞ", "ヷ"], ["ｦﾞ", "ヺ"],
    ["ｶﾞ", "ガ"], ["ｷﾞ", "ギ"], ["ｸﾞ", "グ"], ["ｹﾞ", "ゲ"], ["ｺﾞ", "ゴ"],
    ["ｻﾞ", "ザ"], ["ｼﾞ", "ジ"], ["ｽﾞ", "ズ"], ["ｾﾞ", "ゼ"], ["ｿﾞ", "ゾ"],
    ["ﾀﾞ", "ダ"], ["ﾁﾞ", "ヂ"], ["ﾂﾞ", "ヅ"], ["ﾃﾞ", "デ"], ["ﾄﾞ", "ド"],
    ["ﾊﾞ", "バ"], ["ﾋﾞ", "ビ"], ["ﾌﾞ", "ブ"], ["ﾍﾞ", "ベ"], ["ﾎﾞ", "ボ"],
    ["ﾊﾟ", "パ"], ["ﾋﾟ", "ピ"], ["ﾌﾟ", "プ"], ["ﾍﾟ", "ペ"], ["ﾎﾟ", "ポ"],
    ["ｱ", "ア"], ["ｲ", "イ"], ["ｳ", "ウ"], ["ｴ", "エ"], ["ｵ", "オ"],
    ["ｶ", "カ"], ["ｷ", "キ"], ["ｸ", "ク"], ["ｹ", "ケ"], ["ｺ", "コ"],
    ["ｻ", "サ"], ["ｼ", "シ"], ["ｽ", "ス"], ["ｾ", "セ"], ["ｿ", "ソ"],
    ["ﾀ", "タ"], ["ﾁ", "チ"], ["ﾂ", "ツ"], ["ﾃ", "テ"], ["ﾄ", "ト"],
    ["ﾅ", "ナ"], ["ﾆ", "ニ"], ["ﾇ", "ヌ"], ["ﾈ", "ネ"], ["ﾉ", "ノ"],
    ["ﾊ", "ハ"], ["ﾋ", "ヒ"], ["ﾌ", "フ"], ["ﾍ", "ヘ"], ["ﾎ", "ホ"],
    ["ﾏ", "マ"], ["ﾐ", "ミ"], ["ﾑ", "ム"], ["ﾒ", "メ"], ["ﾓ", "モ"],
    ["ﾔ", "ヤ"], ["ﾕ", "ユ"], ["ﾖ", "ヨ"],
    ["ﾗ", "ラ"], ["ﾘ", "リ"], ["ﾙ", "ル"], ["ﾚ", "レ"], ["ﾛ", "ロ"],
    ["ﾜ", "ワ"], ["ｦ", "ヲ"], ["ﾝ", "ン"],
    ["ｧ", "ァ"], ["ｨ", "ィ"], ["ｩ", "ゥ"], ["ｪ", "ェ"], ["ｫ", "ォ"],
    ["ｬ", "ャ"], ["ｭ", "ュ"], ["ｮ", "ョ"],
    ["ｯ", "ッ"],
    ["｡", "。"], ["｢", "「"], ["｣", "」"], ["､", "、"], ["･", "・"], ["ｰ", "ー"],
  ];

  /** 最初に含まれるスペースより前の文字列を返す。 */
  static getTextBeforeSpace(text: string): string {
    const indexOfSpace = this.getIndexOfSpace(text);
    if (indexOfSpace === -1) { return text; }
    return text.substring(0, indexOfSpace);
  }

  /** 最初に含まれるスペースより後の文字列を返す。 */
  static getTextAfterSpace(text: string): string {
    const indexOfSpace = this.getIndexOfSpace(text);
    if (indexOfSpace === -1) { return ""; }
    return text.substring(indexOfSpace + 1);
  }

  /** 文字列に含まれるカナを半角から全角に変換する。 */
  static convertKanaFromHalfToFull(text: string): string {
    let result = "";
    while (text !== "") {
      const [halfKatakanaText, others] = this.splitWhile(text, x => this.isHalfKatakana(x));
      const [otherText, nextText] = this.splitWhile(others,  x => !this.isHalfKatakana(x));
      const fullKanaText = this.toFullKatakana(halfKatakanaText);
      result = result.concat(fullKanaText, otherText);
      text = nextText;
    }
    return result;
  }

  /** 文字列に含まれるカタカナをひらがなに変換する。ただし、カタカナは全角で正規化されているとする。 */
  static convertKatakanaToHiragana(text: string): string {
    let result = "";
    while (text !== "") {
      const [katakana, others] = this.splitWhile(text, x => this.isFullKatakana(x));
      const [otherText, nextText] = this.splitWhile(others,  x => !this.isFullKatakana(x));
      const hiragana = this.toHiragana(katakana);
      result = result.concat(hiragana, otherText);
      text = nextText;
    }
    return result;
  }

  /** 文字列に含まれる全角カナ、記号、英数を半角に変換する。 */
  static convertFromFullToHalf(text: string): string {
    let result = "";
    while (text !== "") {
      const [fullText, others] = this.splitWhile(text, x => this.isFull(x));
      const [otherText, nextText] = this.splitWhile(others,  x => !this.isFull(x));
      const halfText = this.toHalf(fullText);
      result = result.concat(halfText, otherText);
      text = nextText;
    }
    return result;
  }
  
  /** 文字列を指定した条件で2つのグループに分割する */
  static splitWhile(text: string, predicate: (x: string) => boolean): [string, string] {
    const head = Enumerable.from(text).takeWhile(predicate).toJoinedString();
    const tail = Enumerable.from(text).skipWhile(predicate).toJoinedString();
    return [head, tail];
  }

  /** 文字が半角カタカナか */
  private static isHalfKatakana(character: string): boolean {
    const codePoint = this.codePointAt(character, 0);
    return 0xff61 <= codePoint && codePoint <= 0xff9f; // unicode半角カタカナ
  }

  /** 文字が全角カタカナか */
  private static isFullKatakana(character: string): boolean {
    const codePoint = this.codePointAt(character, 0);
    return 0x30a1 <= codePoint && codePoint <= 0x30ff; // unicode全角カタカナ
  }

  /** 文字が全角か */
  private static isFull(character: string): boolean {
    const codePoint = this.codePointAt(character, 0);
    return ((0xff01 <= codePoint && codePoint <= 0xff5E) // unicode全角英数字、記号
      || (0x30a1 <= codePoint && codePoint <= 0x30ff) // unicode全角カタカナ
      || (0x2018 <= codePoint && codePoint <= 0x201f) // unicode全角シングル、ダブルクォーテーション
      || (0x3000 <= codePoint && codePoint <= 0x301b)); // unicode全角スペース、句読点、括弧、記号
  }

  /** 半角カタカナを全角カタカナに変換する */
  private static toFullKatakana(halfKatakana: string): string {
    // halfKatakana.normalize("NFKC") で変換可能だがieが未対応なので対応表で変換
    const result = StringUtility._replaceTableHalfFullKatakana
      .reduce((p, c) => p.replace(new RegExp(c[0], "g"), c[1]), halfKatakana);
    return result;
  }

  /** カタカナ文字列をひらがな文字列に変換する */
  private static toHiragana(katakanaText: string): string {
    const hiraganaText = Enumerable.from(katakanaText).select(katakanaChar => {
      const katakanaCodePoint = this.codePointAt(katakanaChar, 0);
      const hiraganaCodePoint = katakanaCodePoint - 0x60;
      const hiraganaChar = this.fromCodePoint(hiraganaCodePoint);
      return hiraganaChar;
    }).toJoinedString();
    return hiraganaText;
  }

  /** 全角を半角に変換する */
  private static toHalf(full: string): string {
    const result = StringUtility._replaceTableHalfFullKatakana
      .reduce((p, c) => p.replace(new RegExp(c[1], "g"), c[0]), full);
    return result
      .replace(/[！-～]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
      .replace(/　/g, " ")
      .replace(/￥/g, "\\")
      .replace(/[“”]/g, "\"")
      .replace(/[‘’]/g, "\'");
  }
  
  /**
   * 半角スペースがあればそのインデックスを返す。
   * 半角スペースがない場合、全角スペースのインデックスを返す。
   * 全角半角スペースがない場合、-1を返す。
   */
  private static getIndexOfSpace(text: string): number {
    const indexOfHalfWidthSpace = text.indexOf(" ");
    if (indexOfHalfWidthSpace !== -1) {
      return indexOfHalfWidthSpace;
    }
    const indexOfFullWidhtSpace = text.indexOf("　");
    return indexOfFullWidhtSpace;
  }

  private static codePointAt(text: string, position: number): number {
    // ieでcodePointAtが未対応、ひらがなカタカナはBMPコードポイントに含まれるためcharCodeAtで対応
    return text.charCodeAt(position);
  }

  private static fromCodePoint(codePoint: number): string {
    // ieでfromCodePointが未対応、ひらがなカタカナはBMPコードポイントに含まれるためfromCharCodeで対応
    return String.fromCharCode(codePoint);
  }
}
